import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import EventCard from 'ripple/Events/Components/EventCard';
import { Paper, CardHeader, Grid, Skeleton } from '@mui/material';
import ImageList, { GRID_COLS } from 'ripple/Components/GridList';
import screenSize from 'ripple/Utils/screenSize';
import ImageListItem from '@mui/material/ImageListItem';
import GridTileScroller from 'ripple/Components/GridTileScroller';
import useIsMobile from 'lib/useIsMobile';
import classes from 'ripple/Events/Containers/ListViews/Events/Events.scss';
import { useLazyQuery } from '@apollo/client';
import { EVENT_LIST_INFO } from '../../../../../schema/apolloQueries/eventQueries';

const EventList = ({
  eventType,
  eventsPageSize,
  relay,
  viewer,
  title,
  flexy = true,
  id,
  vertical,
  hoverHandler,
  leftHandler,
  setEventData,
  eventData: data,
  ...props
}) => {
  const sSize = screenSize();
  const [pageSize, setPageSize] = useState(GRID_COLS[sSize]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsPageNo, setEventsPageNo] = useState(1);
  const mobile = useIsMobile();
  const imageListItemRef = useRef(null); // Create a ref for ImageListItem

  const [getEventList] = useLazyQuery(EVENT_LIST_INFO);

  const loadEvents = (pageNo, callBack) => {
    setEventsLoading(true);
    return getEventList({
      variables: {
        page: pageNo,
        size: eventsPageSize,
        eventType,
      },
    })
      .then(res => {
        setEventData(p => ({
          ...(res.data.discoverEvents || p || {}),
          events: [
            ...(p?.events || [] || []),
            ...res.data.discoverEvents.events,
          ],
        }));
        if (callBack) {
          callBack();
        }
        setTimeout(() => {
          setEventsLoading(false);
        }, 1000);
      })
      .catch(() => {
        setEventsLoading(false);
      });
  };

  useEffect(() => {
    loadEvents(1);
  }, []);

  const loadNextPage = callBack => {
    const updatedPageNo = Number(eventsPageNo || 0) + 1;
    setEventsPageNo(updatedPageNo);
    return loadEvents(updatedPageNo, callBack);
  };

  useEffect(() => {
    setPageSize(GRID_COLS[sSize]);
  }, [sSize]);

  const { totalCount, events = [] } = data;

  const totalPages = Math.ceil(totalCount / pageSize);

  const hasNextValidPage = pageNumber => {
    return pageNumber > 0 && pageNumber <= totalPages;
  };

  const hasNext = hasNextValidPage(eventsPageNo);
  return eventsLoading || data?.events?.length > 0 ? (
    <Paper classes={{ root: classes.root }} {...props}>
      <CardHeader
        title={title}
        classes={{ root: classes.headerRoot, title: classes.headerTitle }}
      />
      <GridTileScroller
        hasNextPage={hasNext}
        totalCount={totalCount}
        done={!hasNext}
        loading={eventsLoading}
        doneText='Be sure to check back soon for new events to discover!'
        empty={totalCount === 0}
        emptyText='Check back soon for new events'
        emptyTitle='There are no events right now!'
        pageSize={pageSize}
        loadNextPage={loadNextPage}
        scrollerId={id}
        vertical={vertical}
        leftHandler={leftHandler}
      >
        <ImageList id={id} flexy={events?.length === 1 ? false : flexy} gap={8}>
          {events.map(event => {
            return (
              <ImageListItem
                ref={imageListItemRef}
                key={event.id}
                onMouseEnter={e => {
                  if (!mobile) {
                    const getTarget = e.target.getClientRects();
                    const rects = getTarget && getTarget.length ? getTarget[0] : null;
                    const getRef = imageListItemRef?.current?.getBoundingClientRect();

                    hoverHandler(
                      event,
                      viewer,
                      pageSize,
                      {
                        bottom: rects.bottom,
                        height: getRef.height,
                        left: rects.left,
                        right: rects.right,
                        top: rects.top,
                        width: getRef.width,
                        x: rects.x,
                        y: rects.y,
                      },
                      title,
                      {
                        eventsPageNo,
                        eventsPageSize,
                        eventType,
                      },
                      // e
                    );
                  }
                }}
                onClick={e => {
                  if (mobile) {
                    const getTarget = e.target.getClientRects();
                    const rects = getTarget && getTarget.length ? getTarget[0] : null;
                    const getRef = imageListItemRef?.current?.getBoundingClientRect();
                    hoverHandler(
                      event,
                      viewer,
                      pageSize,
                      {
                        bottom: rects.bottom,
                        height: getRef.height,
                        left: rects.left,
                        right: rects.right,
                        top: rects.top,
                        width: getRef.width,
                        x: rects.x,
                        y: rects.y,
                      },
                      title,
                      {
                        eventsPageNo,
                        eventsPageSize,
                        eventType,
                      },
                    );
                  }
                }}
              >
                <EventCard
                  setEventData={setEventData}
                  eventsPageNo={eventsPageNo}
                  eventsPageSize={eventsPageSize}
                  eventType={eventType}
                  event={event}
                  card
                  key={event.id}
                  viewer={viewer}
                  data={data}
                />
              </ImageListItem>
            );
          })}
          {eventsLoading && !data.events && (
            <Grid container spacing={1}>
              <Grid item sm={6} md={4} lg={2}>
                <Skeleton variant='rectangular' width={220} height={120} />
              </Grid>
              <Grid item sm={6} md={4} lg={2}>
                <Skeleton variant='rectangular' width={220} height={120} />
              </Grid>
              <Grid item sm={6} md={4} lg={2}>
                <Skeleton variant='rectangular' width={220} height={120} />
              </Grid>
              <Grid item sm={6} md={4} lg={2}>
                <Skeleton variant='rectangular' width={220} height={120} />
              </Grid>
            </Grid>
          )}
        </ImageList>
      </GridTileScroller>
    </Paper>
  ) : (
    ''
  );
};

EventList.propTypes = {
  relay: PropTypes.object.isRequired,
  viewer: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  flexy: PropTypes.bool,
  id: PropTypes.string,
  eventsPageSize: PropTypes.number.isRequired,
  eventType: PropTypes.string.isRequired,
  vertical: PropTypes.bool,
  hoverHandler: PropTypes.func.isRequired,
  leftHandler: PropTypes.func.isRequired,
  setEventData: PropTypes.any,
  eventData: PropTypes.any,
};

export default EventList;
